import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import DocumentPage from "../../components/document-page";
import Button from "../../components/button";
import Spinner from "../../components/spinner";
import AddDeviceModal from "../../components/add-device-modal";
import DeleteDeviceModal from "../../components/delete-device-modal";
import RenameDeviceModal from "../../components/rename-device-modal";
import EditSvg from "../../components/icons/edit";
import TrashSvg from "../../components/icons/trash";
import styles from "./style.module.css";
import { DataContext } from "../../context/dataProvider";
import { SUBSCRIPTION_TYPE } from "../../context/types";
import { getDevices, deleteDevice, renameDevice, HwidDeviceApi } from "../../api/user-server";
import { openExternalUrl } from "../../utils/telegram";
import { HelpLink } from "../../common/help";

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  windows: "Windows",
  macos: "macOS",
  linux: "Linux"
};

function getDeviceName(device: HwidDeviceApi, index: number): string {
  if (device.customName?.trim()) return device.customName.trim();
  if (device.deviceModel) return device.deviceModel;
  if (device.platform) return PLATFORM_LABELS[device.platform.toLowerCase()] ?? device.platform;
  return `Устройство ${index + 1}`;
}

function DevicesPage() {
  const context = useContext(DataContext);
  const navigate = useNavigate();
  const userServer = context?.user?.userServer ?? null;
  const isBusiness = Boolean(context?.user?.business);

  const [devices, setDevices] = useState<HwidDeviceApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMenuHwid, setOpenMenuHwid] = useState<string | null>(null);
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<HwidDeviceApi | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState<HwidDeviceApi | null>(null);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await getDevices();
        if (!cancelled) setDevices(result.devices);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteDevice(deleteTarget.hwid);
      setDevices(result.devices);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleRenameConfirm = async (name: string) => {
    if (!renameTarget) return;
    setRenaming(true);
    try {
      const result = await renameDevice(renameTarget.hwid, name);
      setDevices(result.devices);
      setRenameTarget(null);
    } finally {
      setRenaming(false);
    }
  };

  const handleContactUs = () => {
    openExternalUrl(HelpLink);
  };

  if (!userServer) {
    return (
      <DocumentPage variant="scrollable">
        <h1>Подключенные устройства</h1>
        <p className={styles.subtitle}>Подписка не найдена</p>
      </DocumentPage>
    );
  }

  const deviceLimit = userServer.deviceLimit ?? 0;
  const isExpired = new Date(userServer.subscriptionEndDate) < new Date();
  const isTrial = userServer.subscriptionType === SUBSCRIPTION_TYPE.TEST;
  const isConnected = !isExpired && userServer.isEnabled;
  const isAtDeviceLimit = devices.length >= deviceLimit;

  const handleAddDeviceClick = () => {
    // Лимит исчерпан — на тарифы (увеличить / продлить). Иначе — настройка подключений.
    if (isAtDeviceLimit) {
      navigate("/select-tariff");
    } else {
      navigate("/settings");
    }
  };

  let banner: React.ReactNode = null;
  if (isExpired) {
    banner = (
      <div className={styles.banner}>
        <div>
          <div className={styles.bannerTitle}>
            {isTrial ? "Пробная версия истекла" : "Подписка истекла"}
          </div>
          <div className={styles.bannerSubtitle}>Оплатите подписку, чтобы вернуть подключение</div>
        </div>
        <button
          type="button"
          className={styles.bannerBtn}
          onClick={() => navigate("/select-tariff")}
        >
          Оплатить
        </button>
      </div>
    );
  } else if (!isBusiness) {
    banner = (
      <div className={styles.banner}>
        <div>
          <div className={styles.bannerTitle}>Нужно больше устройств?</div>
          <div className={styles.bannerSubtitle}>Настройте количество</div>
        </div>
        <button
          type="button"
          className={styles.bannerBtn}
          onClick={() => (isTrial ? navigate("/select-tariff") : setAddDeviceOpen(true))}
        >
          Настроить
        </button>
      </div>
    );
  }

  return (
    <DocumentPage variant="scrollable">
      <h1>Подключенные устройства</h1>
      <p className={styles.subtitle}>
        {devices.length} из {deviceLimit} устройств
      </p>

      {banner}

      {loading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.deviceList}>
          {devices.map((device, index) => (
            <div className={styles.deviceRow} key={device.hwid}>
              <div className={styles.deviceIndex}>{index + 1}</div>
              <div className={styles.deviceInfo}>
                <div className={styles.deviceName}>{getDeviceName(device, index)}</div>
                <div className={styles.deviceStatus}>
                  {isConnected ? "Подключено" : "Отключено"}
                </div>
              </div>
              <button
                type="button"
                className={styles.editBtn}
                onClick={() =>
                  setOpenMenuHwid(openMenuHwid === device.hwid ? null : device.hwid)
                }
                aria-label="Действия с устройством"
              >
                <EditSvg />
              </button>

              {openMenuHwid === device.hwid && (
                <>
                  <div
                    className={styles.menuBackdrop}
                    role="presentation"
                    onClick={() => setOpenMenuHwid(null)}
                  />
                  <div className={styles.deviceMenu}>
                    <button
                      type="button"
                      className={styles.menuItem}
                      onClick={() => {
                        setOpenMenuHwid(null);
                        setRenameTarget(device);
                      }}
                    >
                      <EditSvg />
                      Переименовать
                    </button>
                    <button
                      type="button"
                      className={styles.menuItemDanger}
                      onClick={() => {
                        setOpenMenuHwid(null);
                        setDeleteTarget(device);
                      }}
                    >
                      <TrashSvg />
                      Удалить
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {userServer.isEnabled && (
        <Button
          className={styles.addDeviceBtn}
          label="Добавить устройство"
          onClick={handleAddDeviceClick}
        />
      )}

      <div className={styles.supportCard}>
        <div className={styles.bannerTitle}>Кто-то добавляет устройства по вашей подписке?</div>
        <p className={styles.supportText}>
          Свяжитесь с нами, чтобы мы обновили конфигурацию и сбросили устройства
        </p>
        <Button label="Связаться с нами" variant="secondary" onClick={handleContactUs} />
      </div>

      {!isBusiness && (
        <AddDeviceModal
          open={addDeviceOpen}
          onClose={() => setAddDeviceOpen(false)}
          userServer={userServer}
        />
      )}

      <DeleteDeviceModal
        open={Boolean(deleteTarget)}
        deviceName={deleteTarget ? getDeviceName(deleteTarget, devices.indexOf(deleteTarget)) : ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDeleteConfirm()}
        loading={deleting}
      />

      <RenameDeviceModal
        open={Boolean(renameTarget)}
        initialName={
          renameTarget ? getDeviceName(renameTarget, devices.indexOf(renameTarget)) : ""
        }
        onClose={() => setRenameTarget(null)}
        onConfirm={(name) => void handleRenameConfirm(name)}
        loading={renaming}
      />
    </DocumentPage>
  );
}

export default DevicesPage;
